/**
 * Maps Firebase Auth errors to short, user-facing copy. Falls back safely when the code is unknown.
 */
const DEFAULT_MESSAGE =
  "Something went wrong. Please try again in a moment.";

const MESSAGES: Record<string, string> = {
  "auth/email-already-in-use":
    "That email is already in use. Sign in instead, or use a different email.",
  "auth/invalid-email": "That doesn’t look like a valid email address.",
  "auth/weak-password":
    "Use a stronger password (try a longer phrase or mix of letters and numbers).",
  "auth/user-not-found":
    "We couldn’t find an account with that email. Check the spelling or create an account.",
  "auth/wrong-password": "That password doesn’t match. Try again or reset your password.",
  "auth/invalid-credential":
    "Email or password doesn’t match. Double-check and try again.",
  "auth/invalid-login-credentials":
    "Email or password doesn’t match. Double-check and try again.",
  "auth/too-many-requests":
    "Too many attempts. Wait a few minutes, then try again.",
  "auth/network-request-failed":
    "Network issue. Check your connection and try again.",
  "auth/popup-closed-by-user": "Sign-in was cancelled. Try again when you’re ready.",
  "auth/cancelled-popup-request": "Sign-in was interrupted. Please try again.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in window. Allow popups for this site, then try again.",
  "auth/account-exists-with-different-credential":
    "An account with this email already exists using a different sign-in method. Try signing in with email, Google, or Apple.",
  "auth/requires-recent-login":
    "For security, sign out and sign back in, then try again.",
  "auth/user-disabled":
    "This account can’t be used to sign in. Contact support if you need help.",
  "auth/operation-not-allowed":
    "This sign-in provider isn’t enabled for this Firebase project. Try another method, or enable the provider in Firebase Console → Authentication → Sign-in methods (and complete any required app settings, e.g. Apple Services ID or Facebook App ID + secret).",
  "auth/missing-email": "Enter your email address.",
  "auth/missing-password": "Enter your password.",
  "auth/internal-error":
    "Something went wrong on our end. Please try again in a moment.",
  "auth/invalid-verification-code": "That code isn’t valid. Request a new one and try again.",
  "auth/invalid-action-code":
    "That verification link is invalid or has already been used. Request a new one from settings.",
  "auth/expired-action-code": "That link or code has expired. Request a new one.",
};

function getAuthErrorCode(error: unknown): string | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "string"
  ) {
    return (error as { code: string }).code;
  }
  return null;
}

function extractCodeFromFirebaseMessage(message: string): string | null {
  const match = message.match(/auth\/[a-z0-9-]+/i);
  return match ? match[0] : null;
}

function looksLikeRawFirebaseMessage(message: string): boolean {
  return /Firebase:\s*Error\s*\(\s*auth\//i.test(message);
}

/**
 * @param error - Firebase `AuthError`, `Error`, or a string (e.g. from a failed API result).
 */
export function getFirebaseAuthErrorMessage(error: unknown): string {
  const code = getAuthErrorCode(error);
  if (code && MESSAGES[code]) {
    return MESSAGES[code];
  }

  if (typeof error === "string") {
    const fromString = extractCodeFromFirebaseMessage(error);
    if (fromString && MESSAGES[fromString]) {
      return MESSAGES[fromString];
    }
    if (!looksLikeRawFirebaseMessage(error)) {
      return error || DEFAULT_MESSAGE;
    }
    return DEFAULT_MESSAGE;
  }

  if (error instanceof Error) {
    const fromMessage = extractCodeFromFirebaseMessage(error.message);
    if (fromMessage && MESSAGES[fromMessage]) {
      return MESSAGES[fromMessage];
    }
    if (!looksLikeRawFirebaseMessage(error.message)) {
      return error.message || DEFAULT_MESSAGE;
    }
    return DEFAULT_MESSAGE;
  }

  return DEFAULT_MESSAGE;
}
